import { Close, Healing, Info, NewReleases, Update, Warning } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Divider,
  Fade,
  IconButton,
  Modal,
  Paper,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import type { Announcement, AnnouncementType } from '@/types';
import RichContent from './rich-content';

// Helper function for announcement styling
const getAnnouncementConfig = (type: AnnouncementType) => {
  switch (type) {
    case 'warning':
      return {
        label: 'Wichtiger Hinweis',
        color: 'warning' as const,
        icon: <Warning />,
      };
    case 'info':
      return {
        label: 'Information',
        color: 'info' as const,
        icon: <Info />,
      };
    case 'fix':
      return {
        label: 'Fix',
        color: 'primary' as const,
        icon: <Healing />,
      };
    case 'feature':
      return {
        label: 'Neues Feature',
        color: 'primary' as const,
        icon: <Update />,
      };
    case 'release':
      return {
        label: 'Release',
        color: 'success' as const,
        icon: <NewReleases />,
      };
  }
};

export default function AnnouncementsModal({
  open,
  onClose,
  announcements,
  onMarkAsViewed,
  isLoading = false,
}: {
  open: boolean;
  onClose: () => void;
  announcements: Announcement[];
  onMarkAsViewed?: () => void;
  isLoading?: boolean;
}) {
  const handleMarkAsViewed = () => {
    if (onMarkAsViewed) {
      onMarkAsViewed();
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        backgroundColor: 'rgba(0, 0, 0, 0.8)', // Darker overlay
      }}>
      <Fade in={open} timeout={{ enter: 300, exit: 200 }}>
        <Paper
          sx={{
            maxWidth: { xs: '100%', sm: 600 },
            width: '100%',
            maxHeight: { xs: '90vh', sm: '80vh' },
            borderRadius: 2,
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
          }}>
          {/* Header - Fixed */}
          <Box
            sx={{
              p: 3,
              pb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NewReleases color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Neuigkeiten
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>

          <Divider />

          {/* Content - Scrollable */}
          <Box
            sx={{
              p: 3,
              overflowY: 'auto',
              flex: 1,
              minHeight: 0, // Important for flex scrolling
            }}>
            {announcements.map((announcement, index) => {
              const config = getAnnouncementConfig(announcement.type);

              return (
                <Box key={announcement.id} sx={{ mb: index < announcements.length - 1 ? 3 : 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 1,
                    }}>
                    <Box>
                      <Chip
                        label={config.label}
                        color={config.color}
                        variant="outlined"
                        size="small"
                        icon={config.icon}
                      />
                      {announcement.releaseVersion && (
                        <Chip
                          label={announcement.releaseVersion}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                    <Typography color="text.secondary">
                      {dayjs(announcement.publishedAt).format('DD.MM.YYYY')}
                    </Typography>
                  </Box>

                  <Typography variant="h6" sx={{ mb: 1 }}>
                    <RichContent content={announcement.headlineNode} />
                  </Typography>

                  <RichContent content={announcement.contentNode} />

                  {index < announcements.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              );
            })}

            {/* Footer Button - Inside scrollable area */}
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'flex-end',
              }}>
              {onMarkAsViewed ? (
                <Button variant="contained" onClick={handleMarkAsViewed} disabled={isLoading}>
                  Als gelesen markieren
                </Button>
              ) : (
                <Button variant="outlined" onClick={onClose}>
                  Schließen
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
}
